import { queryOptions, useMutation } from '@tanstack/react-query'

import {
  cancelSelfHolding,
  changeActivationOrder,
  createSelfHolding,
  getSelfHoldings,
  getSubscribeMapp,
  getSubscribeMappDetail,
  getTicketSubscribeDetail,
  getTicketSubscribeListAll,
  type CancelSelfHoldingParams,
  type CreateSelfHoldingParams,
} from './plan.api'

export const planEntityQueries = {
  all: ['plan'],

  getTicketSubscribeListAllQueryKey: ({ bearerToken }: { bearerToken?: string }) => [
    ...planEntityQueries.all,
    'LIST_ALL',
    bearerToken,
  ],

  getTicketSubscribeDetailQueryKey: ({ ticketIds, bearerToken }: { ticketIds: string; bearerToken?: string }) => [
    ...planEntityQueries.all,
    'DETAIL',
    ticketIds,
    bearerToken,
  ],

  getTicketSubscribeListAll: ({ bearerToken }: { bearerToken?: string }) =>
    queryOptions({
      queryKey: planEntityQueries.getTicketSubscribeListAllQueryKey({ bearerToken }),
      queryFn: async () => await getTicketSubscribeListAll(bearerToken),
    }),

  getTicketSubscribeDetail: ({ ticketIds, bearerToken }: { ticketIds: string; bearerToken?: string }) =>
    queryOptions({
      queryKey: planEntityQueries.getTicketSubscribeDetailQueryKey({ ticketIds, bearerToken }),
      queryFn: async () => await getTicketSubscribeDetail(ticketIds, bearerToken),
    }),
  getSubscribeMappQueryKey: ({ bearerToken }: { bearerToken?: string }) => [
    ...planEntityQueries.all,
    'SUBSCRIBE_MAPP_LIST',
    bearerToken,
  ],

  getSubscribeMapp: ({ bearerToken }: { bearerToken?: string }) =>
    queryOptions({
      queryKey: planEntityQueries.getSubscribeMappQueryKey({ bearerToken }),
      queryFn: async () => await getSubscribeMapp(bearerToken),
    }),

  getSubscribeMappDetailQueryKey: ({
    subscribeMappId,
    bearerToken,
  }: {
    subscribeMappId: string
    bearerToken?: string
  }) => [...planEntityQueries.all, 'SUBSCRIBE_MAPP_DETAIL', subscribeMappId, bearerToken],

  getSubscribeMappDetail: ({ subscribeMappId, bearerToken }: { subscribeMappId: string; bearerToken?: string }) =>
    queryOptions({
      queryKey: planEntityQueries.getSubscribeMappDetailQueryKey({ subscribeMappId, bearerToken }),
      queryFn: async () => await getSubscribeMappDetail(subscribeMappId, bearerToken),
    }),

  getSelfHoldingsQueryKey: ({ subscribeMappId, bearerToken }: { subscribeMappId: string; bearerToken?: string }) => [
    ...planEntityQueries.all,
    'SELF_HOLDINGS',
    subscribeMappId,
    bearerToken,
  ],

  getSelfHoldings: ({ subscribeMappId, bearerToken }: { subscribeMappId: string; bearerToken?: string }) =>
    queryOptions({
      queryKey: planEntityQueries.getSelfHoldingsQueryKey({ subscribeMappId, bearerToken }),
      queryFn: async () => await getSelfHoldings(subscribeMappId, bearerToken),
    }),
} as const

export const useChangeActivationOrderMutation = () => {
  return useMutation({
    mutationFn: async ({
      orderedSubscribeMappIds,
      bearerToken,
    }: {
      orderedSubscribeMappIds: string[]
      bearerToken?: string
    }) => await changeActivationOrder(orderedSubscribeMappIds, bearerToken),
  })
}

export const useCreateSelfHoldingMutation = () => {
  return useMutation({
    mutationFn: async ({
      bearerToken,
      ...params
    }: CreateSelfHoldingParams & {
      bearerToken?: string
    }) => await createSelfHolding(params, bearerToken),
  })
}

export const useCancelSelfHoldingMutation = () => {
  return useMutation({
    mutationFn: async ({
      bearerToken,
      ...params
    }: CancelSelfHoldingParams & {
      bearerToken?: string
    }) => await cancelSelfHolding(params, bearerToken),
  })
}
